import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Car, 
  Users, 
  DollarSign, 
  MapPin,
  Clock,
  Star,
  Calendar,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Fade,
  Slide,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StatsCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  }
}));

const ProgressCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 12,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const IconContainer = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
  boxShadow: `0 4px 20px ${color}40`,
  marginBottom: theme.spacing(2),
}));

const RideStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading statistics
    const loadStats = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockStats = {
        overview: {
          totalRides: 156,
          totalConnects: 89,
          moneySaved: 12450,
          averageRating: 4.8,
          monthlyRides: 24,
          monthlySavings: 1850
        },
        trends: {
          ridesGrowth: 12.5,
          savingsGrowth: 8.3,
          ratingGrowth: 2.1,
          connectsGrowth: -3.2
        },
        monthlyData: [
          { month: 'Jan', rides: 18, savings: 1200 },
          { month: 'Feb', rides: 22, savings: 1450 },
          { month: 'Mar', rides: 19, savings: 1350 },
          { month: 'Apr', rides: 25, savings: 1650 },
          { month: 'May', rides: 28, savings: 1850 },
          { month: 'Jun', rides: 24, savings: 1600 }
        ],
        topRoutes: [
          { route: 'Home → Office', frequency: 45, savings: 3200 },
          { route: 'Airport → Home', frequency: 12, savings: 1800 },
          { route: 'Mall → Home', frequency: 8, savings: 600 },
          { route: 'Gym → Home', frequency: 15, savings: 900 }
        ],
        goals: {
          monthlyRides: { target: 30, current: 24, progress: 80 },
          monthlySavings: { target: 2000, current: 1850, progress: 92.5 },
          rating: { target: 4.5, current: 4.8, progress: 100 },
          connects: { target: 100, current: 89, progress: 89 }
        }
      };
      
      setStats(mockStats);
      setLoading(false);
    };

    loadStats();
  }, []);

  const getTrendIcon = (value) => {
    return value > 0 ? <TrendingUp size={16} color="#10b981" /> : <TrendingDown size={16} color="#ef4444" />;
  };

  const getTrendColor = (value) => {
    return value > 0 ? '#10b981' : '#ef4444';
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Ride Statistics
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Slide direction="up" in timeout={600}>
        <Box>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Your Ride Statistics
          </Typography>

          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={800}>
                <StatsCard>
                  <CardContent>
                    <IconContainer color="#6366f1">
                      <Car size={24} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stats.overview.totalRides}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Rides
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTrendIcon(stats.trends.ridesGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(stats.trends.ridesGrowth) }}>
                        +{stats.trends.ridesGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={900}>
                <StatsCard>
                  <CardContent>
                    <IconContainer color="#10b981">
                      <DollarSign size={24} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ₹{stats.overview.moneySaved.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Saved
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTrendIcon(stats.trends.savingsGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(stats.trends.savingsGrowth) }}>
                        +{stats.trends.savingsGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1000}>
                <StatsCard>
                  <CardContent>
                    <IconContainer color="#f59e0b">
                      <Star size={24} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stats.overview.averageRating}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Average Rating
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTrendIcon(stats.trends.ratingGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(stats.trends.ratingGrowth) }}>
                        +{stats.trends.ratingGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Fade in timeout={1100}>
                <StatsCard>
                  <CardContent>
                    <IconContainer color="#a855f7">
                      <Users size={24} color="white" />
                    </IconContainer>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stats.overview.totalConnects}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      Total Connects
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTrendIcon(stats.trends.connectsGrowth)}
                      <Typography variant="caption" sx={{ color: getTrendColor(stats.trends.connectsGrowth) }}>
                        {stats.trends.connectsGrowth}%
                      </Typography>
                    </Box>
                  </CardContent>
                </StatsCard>
              </Fade>
            </Grid>
          </Grid>

          {/* Goals Progress */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Monthly Goals Progress
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <ProgressCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Monthly Rides
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {stats.goals.monthlyRides.current}/{stats.goals.monthlyRides.target}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={stats.goals.monthlyRides.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </ProgressCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ProgressCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Monthly Savings
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      ₹{stats.goals.monthlySavings.current}/{stats.goals.monthlySavings.target}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={stats.goals.monthlySavings.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </ProgressCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ProgressCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Rating Target
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {stats.goals.rating.current}/{stats.goals.rating.target}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={stats.goals.rating.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </ProgressCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ProgressCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Connects Used
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      {stats.goals.connects.current}/{stats.goals.connects.target}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={stats.goals.connects.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </ProgressCard>
              </Grid>
            </Grid>
          </Box>

          {/* Top Routes */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Your Top Routes
            </Typography>
            <Grid container spacing={2}>
              {stats.topRoutes.map((route, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Fade in timeout={1200 + index * 100}>
                    <StatsCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {route.route}
                          </Typography>
                          <Chip 
                            label={`${route.frequency} rides`}
                            size="small"
                            color="primary"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MapPin size={16} color="#6366f1" />
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {route.frequency} times
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DollarSign size={16} color="#10b981" />
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              ₹{route.savings} saved
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </StatsCard>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default RideStatistics; 